import { useCallback, useEffect, useMemo, useState } from 'react'

interface FilterOptions {
  filterValue: string
  items: Array<any>
  propertiesToFilter?: Array<string>
}

export const useFilter = ({ filterValue, items, propertiesToFilter = [] }: FilterOptions) => {
  const [filteredData, setFilteredData] = useState<Array<any>>([])

  const sexMap = useMemo(
    () => ({
      F: 'Fêmea',
      M: 'Macho'
    }),
    []
  )

  const normalizeString = useCallback((str: string): string => {
    if (typeof str !== 'string') {
      return String(str).toLowerCase()
    }
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
  }, [])

  const isDate = useCallback((value: any): boolean => {
    if (typeof value !== 'string') {
      return false
    }

    // Não considerar números simples como datas (ex: 1818, 181, etc)
    if (/^\d{1,4}$/.test(value)) {
      return false
    }

    // Verificar se é uma string de data válida (formato ISO, dd/mm/yyyy, yyyy-mm-dd, etc)
    if (!/\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}|\d{4}\/\d{2}\/\d{2}/.test(value)) {
      return false
    }

    const date = new Date(value)
    return !isNaN(date.getTime())
  }, [])

  const formatDate = useCallback((dateString: string): string => {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()

    return `${day}/${month}/${year}`
  }, [])

  const isPartialDateMatch = useCallback((dateString: string, filter: string): boolean => {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return false

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()

    if (filter.length === 10 && filter.includes('/')) {
      const [filterDay, filterMonth, filterYear] = filter.split('/')
      return day === filterDay && month === filterMonth && year === Number(filterYear)
    }

    if (filter.length === 5 && filter.includes('/')) {
      const [filterDay, filterMonth] = filter.split('/')
      return day === filterDay && month === filterMonth
    }

    if (filter.length === 2 && !filter.includes('/')) {
      return day === filter
    }

    return false
  }, [])

  const formatCurrency = useCallback((value: number): string => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
  }, [])

  const getAllValues = useCallback(
    (obj: any, allowedKeys: string[]): string[] => {
      let values: string[] = []

      const extractValues = (currentObj: any, prefix = '') => {
        Object.keys(currentObj).forEach((key) => {
          let value = currentObj[key]
          const fullKey = prefix ? `${prefix}.${key}` : key

          if (value !== null && value !== undefined) {
            if (typeof value === 'object' && !Array.isArray(value)) {
              extractValues(value, fullKey)
            } else {
              if (allowedKeys.length === 0 || allowedKeys.some((prop) => fullKey.endsWith(prop))) {
                // Tratar números
                if (typeof value === 'number') {
                  values.push(value.toString())
                  values.push(formatCurrency(value))
                  return // Não adicionar o valor novamente no final
                }

                // Tratar arrays de strings
                if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
                  values.push(value.join(', '))
                  return // Não adicionar o valor novamente no final
                }

                // Tratar datas
                if (isDate(value)) {
                  if (isPartialDateMatch(value, filterValue)) {
                    values.push(formatDate(value))
                  } else {
                    values.push(formatDate(value))
                  }
                  return // Não adicionar o valor novamente no final
                }

                // Tratar sexo
                if (key.includes('sexo') && sexMap[value]) {
                  values.push(sexMap[value])
                  return // Não adicionar o valor novamente no final
                }

                // Adicionar valor padrão (string)
                values.push(String(value))
              }
            }
          }
        })
      }

      extractValues(obj)
      return values
    },
    [filterValue, isDate, isPartialDateMatch, formatDate, formatCurrency, sexMap]
  )

  const applyFilters = useCallback((): Array<any> => {
    const normalizedFilter = normalizeString(filterValue.trim())

    return items.filter((item) => {
      const values = getAllValues(item, propertiesToFilter).map(normalizeString)
      return values.some((value) => value.includes(normalizedFilter))
    })
  }, [items, propertiesToFilter, normalizeString, getAllValues, filterValue])

  const handleFilter = useCallback(() => {
    if (filterValue) {
      const result = applyFilters()
      setFilteredData(result)
    } else {
      setFilteredData(items)
    }
  }, [filterValue, applyFilters, items])

  const clearFilter = useCallback(() => {
    setFilteredData(items)
  }, [items])

  useEffect(() => {
    setFilteredData(items)
  }, [items])

  return { filteredData, handleFilter, clearFilter }
}
